import { api } from '../api.js';

const WEBHOOKS = [
  { type: 'leaderboard', label: 'Leaderboard',  env: 'DISCORD_LEADERBOARD_WEBHOOK_URL', color: '#ffd700', desc: 'Rank changes in global & per-game leaderboards' },
  { type: 'achievement', label: 'Achievement',   env: 'DISCORD_ACHIEVEMENTS_WEBHOOK_URL', color: '#a855f7', desc: 'Real-time achievement unlock notifications' },
  { type: 'signup',      label: 'Signup',         env: 'DISCORD_WEBHOOK_URL',              color: '#00e5a0', desc: 'New player registration alerts' },
  { type: 'crash',       label: 'Crash Report',   env: 'DISCORD_CRASH_WEBHOOK_URL',        color: '#ff4060', desc: 'Client crash report notifications' },
  { type: 'moderation',  label: 'Moderation',     env: 'DISCORD_MODERATION_WEBHOOK_URL',   color: '#ff4060', desc: 'Content moderation event alerts' },
];

let _rendered = false;

export async function render() {
  if (_rendered) return;
  _rendered = true;

  const container = document.getElementById('integrations-cards');
  if (!container) return;

  container.innerHTML = '<div class="admin-loading">Loading webhook status…</div>';

  let status;
  try {
    status = await api.get('/api/admin/integrations/discord/status');
  } catch {
    container.innerHTML = '<div class="admin-loading">Failed to load webhook status.</div>';
    return;
  }

  container.innerHTML = '';

  const table = document.createElement('table');
  table.style.cssText = 'width:100%; border-collapse:collapse; margin-top:12px';

  for (const wh of WEBHOOKS) {
    const configured = !!status[wh.type];
    const tr = document.createElement('tr');
    tr.style.cssText = 'border-bottom:1px solid var(--border, #333)';

    // Status dot + label + description
    const tdInfo = document.createElement('td');
    tdInfo.style.cssText = 'padding:12px 0; vertical-align:middle';
    tdInfo.innerHTML = `<div style="display:flex; align-items:center; gap:8px; margin-bottom:2px">`
      + `<span style="width:8px; height:8px; border-radius:50%; display:inline-block; background:${configured ? '#22c55e' : '#666'}"></span>`
      + `<strong style="font-size:13px">${wh.label}</strong>`
      + `</div>`
      + `<div style="font-size:11px; opacity:0.6; padding-left:16px">${wh.desc}</div>`
      + (configured
        ? '<div style="font-size:10px; color:#22c55e; padding-left:16px; margin-top:2px">Configured</div>'
        : `<div style="font-size:10px; color:#888; padding-left:16px; margin-top:2px">Not configured (<code style="font-size:10px">${wh.env}</code>)</div>`);

    // Send button + feedback
    const tdAction = document.createElement('td');
    tdAction.style.cssText = 'padding:12px 0; vertical-align:middle; text-align:right; white-space:nowrap';

    const feedback = document.createElement('span');
    feedback.style.cssText = 'font-size:11px; margin-right:10px';

    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = 'Send Sample';
    btn.disabled = !configured;
    if (configured) btn.style.borderColor = wh.color;

    btn.addEventListener('click', async () => {
      btn.disabled = true;
      btn.textContent = 'Sending…';
      feedback.textContent = '';
      try {
        await api.post('/api/admin/integrations/discord/test', { type: wh.type });
        feedback.style.color = '#22c55e';
        feedback.textContent = 'Sent!';
      } catch (err) {
        feedback.style.color = '#ff4060';
        feedback.textContent = err.message || 'Failed';
      }
      btn.disabled = false;
      btn.textContent = 'Send Sample';
    });

    tdAction.append(feedback, btn);
    tr.append(tdInfo, tdAction);
    table.appendChild(tr);
  }

  container.appendChild(table);

  // ── Email test-send ────────────────────────────────────────
  await renderEmailTests();
}

const EMAIL_CATEGORY_ORDER = ['Auth', 'Social', 'Async games', 'Re-engagement', 'Clips', 'Ops'];

async function renderEmailTests() {
  const list = document.getElementById('email-test-list');
  if (!list) return;

  list.innerHTML = '<div class="admin-loading">Loading email templates…</div>';

  let templates;
  try {
    templates = await api.get('/api/admin/email-tests');
  } catch {
    list.innerHTML = '<div class="admin-loading">Failed to load email templates.</div>';
    return;
  }

  // Group by category, preserving a sensible order (known categories first).
  const groups = new Map();
  for (const t of templates) {
    if (!groups.has(t.category)) groups.set(t.category, []);
    groups.get(t.category).push(t);
  }
  const orderedCats = [
    ...EMAIL_CATEGORY_ORDER.filter(c => groups.has(c)),
    ...[...groups.keys()].filter(c => !EMAIL_CATEGORY_ORDER.includes(c)),
  ];

  list.innerHTML = '';
  const recipientEl = document.getElementById('email-test-recipient');

  for (const cat of orderedCats) {
    const header = document.createElement('div');
    header.textContent = cat;
    header.style.cssText = 'font-size:10px; letter-spacing:0.08em; text-transform:uppercase; opacity:0.5; margin:14px 0 4px';
    list.appendChild(header);

    const table = document.createElement('table');
    table.style.cssText = 'width:100%; border-collapse:collapse';

    for (const t of groups.get(cat)) {
      const tr = document.createElement('tr');
      tr.style.cssText = 'border-bottom:1px solid var(--border, #333)';

      const tdLabel = document.createElement('td');
      tdLabel.style.cssText = 'padding:10px 0; vertical-align:middle; font-size:13px';
      tdLabel.textContent = t.label;

      const tdAction = document.createElement('td');
      tdAction.style.cssText = 'padding:10px 0; vertical-align:middle; text-align:right; white-space:nowrap';

      const feedback = document.createElement('span');
      feedback.style.cssText = 'font-size:11px; margin-right:10px';

      const btn = document.createElement('button');
      btn.className = 'btn';
      btn.textContent = 'Send Test';

      btn.addEventListener('click', async () => {
        const email = recipientEl?.value?.trim();
        if (!email) {
          feedback.style.color = '#ff4060';
          feedback.textContent = 'Enter a recipient email above.';
          return;
        }
        btn.disabled = true;
        btn.textContent = 'Sending…';
        feedback.textContent = '';
        try {
          await api.post('/api/admin/email-test', { type: t.type, email });
          feedback.style.color = '#22c55e';
          feedback.textContent = 'Sent!';
        } catch (err) {
          feedback.style.color = '#ff4060';
          feedback.textContent = err.message || 'Failed';
        }
        btn.disabled = false;
        btn.textContent = 'Send Test';
      });

      tdAction.append(feedback, btn);
      tr.append(tdLabel, tdAction);
      table.appendChild(tr);
    }
    list.appendChild(table);
  }
}
